import React, { useState, useEffect, useCallback } from 'react'
import { testSupabaseConnection } from '../../utils/supabaseTest'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import PetService from '../../services/petService'
import { useAuth } from '../../contexts/AuthContext'

const {
  FiDatabase, FiCheckCircle, FiXCircle, FiRefreshCw,
  FiAlertTriangle, FiInfo, FiChevronDown, FiChevronUp,
  FiCopy, FiExternalLink, FiCode, FiUser, FiPlay, FiLogIn, FiUserX
} = FiIcons

// ✅ 模組層級：單一 PetService 實例
const petService = new PetService()

// ✅ 模組層級：SQL 字串只建立一次
const MIGRATION_SQL = `-- PetCare 資料庫遷移 SQL
CREATE TABLE IF NOT EXISTS pets (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  name text NOT NULL,
  type text NOT NULL CHECK (type IN ('dog','cat','bird','fish','rabbit','hamster','turtle','other')),
  breed text, age numeric,
  gender text CHECK (gender IN ('male','female','')),
  weight text, height text, color text, microchip_id text,
  registration_id text, photo_url text, feeding_schedule text,
  portion_size text, can_eat text, cannot_eat text, likes text,
  dislikes text, temperament text, exercise_needs text, training_notes text,
  medications text, medication_schedule text, vaccines text,
  next_vaccine_date date, allergies text, medical_conditions text,
  vet_name text, vet_phone text, insurance_provider text, insurance_policy text,
  special_notes text, share_token text UNIQUE,
  health_status text DEFAULT 'healthy', last_vet_visit date,
  next_appointment date, profile_completion integer DEFAULT 0,
  created_at timestamptz DEFAULT now(), updated_at timestamptz DEFAULT now()
);
ALTER TABLE pets ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Users can view own pets" ON pets FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY "Users can insert own pets" ON pets FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY "Users can update own pets" ON pets FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY "Users can delete own pets" ON pets FOR DELETE TO authenticated USING (auth.uid() = user_id);
CREATE POLICY "Anyone can view pets via share token" ON pets FOR SELECT TO anon, authenticated USING (share_token IS NOT NULL);
CREATE INDEX IF NOT EXISTS pets_user_id_idx ON pets(user_id);
CREATE INDEX IF NOT EXISTS pets_share_token_idx ON pets(share_token);`

// ✅ 模組層級：純展示組件
const StatusIcon = React.memo(({ condition, trueIcon = FiCheckCircle, falseIcon = FiXCircle }) => (
  <SafeIcon
    icon={condition ? trueIcon : falseIcon}
    className={condition ? 'text-green-500' : 'text-red-500'}
  />
))
StatusIcon.displayName = 'StatusIcon'

const SupabaseStatus = () => {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [petServiceStatus, setPetServiceStatus] = useState(null)
  const [testingPetSave, setTestingPetSave] = useState(false)
  const { user, isDemo } = useAuth()

  const checkConnection = useCallback(async () => {
    setLoading(true)
    try {
      const supabaseResult = await testSupabaseConnection()
      setStatus(supabaseResult)
      const serviceStatus = petService.getConnectionStatus?.()
      if (serviceStatus) setPetServiceStatus(serviceStatus)
    } catch (err) {
      console.error('連接檢查失敗:', err)
      setStatus({ connected: false, error: err })
    } finally {
      setLoading(false)
    }
  }, [])

  const copyMigrationSQL = useCallback(() => {
    navigator.clipboard.writeText(MIGRATION_SQL).then(() => {
      alert('✅ SQL 已複製到剪貼板！\n\n請前往 Supabase Dashboard 的 SQL Editor 執行此 SQL。')
    }).catch(err => {
      console.error('複製失敗:', err)
    })
  }, [])

  const testPetSave = useCallback(async () => {
    setTestingPetSave(true)
    try {
      const testPetData = {
        name: `測試寵物-${Date.now()}`,
        type: 'dog',
        breed: '測試品種',
        age: 2
      }
      const savedPet = await petService.savePet(testPetData)
      if (savedPet?.id) {
        const serviceStatus = petService.getConnectionStatus?.()
        const mode = serviceStatus?.usingDemo ? 'Demo 模式' : 'Supabase 模式'
        alert(`✅ 測試成功！\n\n寵物名稱: ${savedPet.name}\nID: ${savedPet.id}\n模式: ${mode}`)
        checkConnection()
      } else {
        alert('❌ 測試失敗：未返回有效的寵物資料')
      }
    } catch (error) {
      console.error('寵物儲存測試失敗:', error)
      alert(`❌ 測試失敗：${error.message}`)
    } finally {
      setTestingPetSave(false)
    }
  }, [checkConnection])

  useEffect(() => {
    if (expanded) checkConnection()
  }, [expanded, checkConnection])

  // ✅ 只在開發環境顯示
  if (process.env.NODE_ENV !== 'development') return null

  const getOverallStatus = () => {
    if (!status) return { color: 'gray', text: '未檢查', bgColor: 'bg-gray-100' }
    if (isDemo) return { color: 'blue', text: 'Demo 模式', bgColor: 'bg-blue-100' }
    if (status.connected && status.tableStructureOk && user) return { color: 'green', text: '正常連接', bgColor: 'bg-green-100' }
    if (status.connected && status.tableStructureOk && !user) return { color: 'yellow', text: '需要登入', bgColor: 'bg-yellow-100' }
    if (status.connected && !status.tableStructureOk) return { color: 'orange', text: '需要設定', bgColor: 'bg-orange-100' }
    return { color: 'red', text: '連接失敗', bgColor: 'bg-red-100' }
  }

  const overallStatus = getOverallStatus()
  const statusTextClass = {
    green: 'text-green-700', yellow: 'text-yellow-700',
    orange: 'text-orange-700', blue: 'text-blue-700',
    red: 'text-red-700', gray: 'text-gray-600'
  }[overallStatus.color]

  return (
    <motion.div
      className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-200 dark:border-gray-700 z-50 max-w-md overflow-hidden"
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${overallStatus.bgColor}`}
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2 flex-1">
          <SafeIcon icon={FiDatabase} className="text-blue-500" />
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Supabase 狀態</h3>
            <p className={`text-xs font-medium ${statusTextClass}`}>{overallStatus.text}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <StatusIcon condition={status.connected && (isDemo || (status.tableStructureOk && user))} />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); checkConnection() }}
            disabled={loading}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className={`text-gray-500 text-sm ${loading ? 'animate-spin' : ''}`} />
          </button>
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-gray-500 text-sm" />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-gray-200 dark:border-gray-700"
          >
            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
                  <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                  <span>檢查連接中...</span>
                </div>
              ) : status ? (
                <>
                  {isDemo && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0 text-sm" />
                        <p className="text-xs text-blue-600 dark:text-blue-400">Supabase 連接失敗，正在使用本地演示資料。功能受限。</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <StatusIcon condition={status.connected} />
                      <span className={`text-xs font-medium ${status.connected ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                        {status.connected ? '✅ 資料庫連接正常' : '❌ 資料庫連接失敗'}
                      </span>
                    </div>
                    {status.connected && (
                      <div className="flex items-center gap-2">
                        <StatusIcon condition={status.tableStructureOk} falseIcon={FiAlertTriangle} />
                        <span className={`text-xs ${status.tableStructureOk ? 'text-green-700 dark:text-green-400' : 'text-yellow-700 dark:text-yellow-400'}`}>
                          {status.tableStructureOk ? '✅ 資料表結構正常' : '⚠️ 需要建立資料表'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <StatusIcon
                        condition={isDemo || !!user}
                        trueIcon={user ? FiUser : FiUserX}
                        falseIcon={FiLogIn}
                      />
                      <span className={`text-xs ${isDemo ? 'text-blue-700 dark:text-blue-400' : user ? 'text-green-700 dark:text-green-400' : 'text-orange-700 dark:text-orange-400'}`}>
                        {isDemo ? '🔄 Demo 模式' : user ? `👤 ${user.email}` : '🔐 需要登入'}
                      </span>
                    </div>
                    {petServiceStatus && (
                      <div className="flex items-center gap-2">
                        <StatusIcon condition={petServiceStatus.connected && petServiceStatus.tableExists} />
                        <span className={`text-xs ${petServiceStatus.usingDemo ? 'text-orange-700 dark:text-orange-400' : petServiceStatus.connected && petServiceStatus.tableExists ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                          {petServiceStatus.usingDemo ? '🔄 PetService: Demo' : petServiceStatus.connected && petServiceStatus.tableExists ? '✅ PetService: 正常' : '❌ PetService: 問題'}
                        </span>
                      </div>
                    )}
                  </div>

                  {!isDemo && status.connected && status.tableStructureOk && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      <div className="flex justify-between"><span>寵物數量:</span><span className="font-medium">{status.petCount || 0}</span></div>
                      <div className="flex justify-between"><span>可分享:</span><span className="font-medium">{status.shareablePets || 0}</span></div>
                    </div>
                  )}

                  {status.error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-2.5">
                      <p className="text-xs text-red-600 dark:text-red-400">{status.error.message}</p>
                    </div>
                  )}

                  {!isDemo && status.connected && status.needsTableCreation && (
                    <div className="space-y-1.5">
                      <button onClick={copyMigrationSQL}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors">
                        <SafeIcon icon={FiCopy} className="text-xs" />
                        <span>複製 SQL 到剪貼板</span>
                      </button>
                      <a href="https://supabase.com/dashboard/project/ktqwxutgkbnygnzwqwar/sql"
                        target="_blank" rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors">
                        <SafeIcon icon={FiExternalLink} className="text-xs" />
                        <span>開啟 SQL Editor</span>
                      </a>
                    </div>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2.5 space-y-1.5">
                    <button
                      onClick={() => window.testSupabase?.()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-600 text-white text-xs rounded-lg hover:bg-gray-700 transition-colors">
                      <SafeIcon icon={FiCode} className="text-xs" />
                      <span>執行控制台測試</span>
                    </button>
                    <button onClick={testPetSave} disabled={testingPetSave}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-white text-xs rounded-lg transition-colors ${testingPetSave ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}>
                      <SafeIcon icon={testingPetSave ? FiRefreshCw : FiPlay} className={`text-xs ${testingPetSave ? 'animate-spin' : ''}`} />
                      <span>{testingPetSave ? '測試中...' : '測試寵物儲存'}</span>
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">點擊展開以檢查狀態</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default SupabaseStatus